import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Backdrop,
  Card,
  CardContent
} from '@mui/material';
import { colors } from 'styles/colors';

interface PaymentProcessingOverlayProps {
  open: boolean;
  courseName?: string;
  message?: string;
  isCourse?: boolean;
}

const PaymentProcessingOverlay: React.FC<PaymentProcessingOverlayProps> = ({
  open,
  courseName,
  message = "Processing your payment...",
  isCourse = false
}) => {
  return (
    <Backdrop
      open={open}
      sx={{
        color: '#fff',
        zIndex: (theme) => theme.zIndex.drawer + 2, // Higher than normal backdrop
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(4px)'
      }}
    >
      <Card 
        sx={{ 
          minWidth: 250,
          maxWidth: 500,
          mx: 2,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}
      >
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          {/* Animated Loading Indicator */}
          <Box sx={{ mb: 3 }}>
            <CircularProgress
              size={50}
              thickness={3}
              sx={{ color: colors.primaryBlue }}
            />
          </Box>

          {/* Main Message */}
          <Typography 
            variant="h6" 
            fontWeight={600} 
            sx={{ 
              color: colors.fontDarkBlue,
              mb: 2,
              fontSize: '18px'
            }}
          >
            {message}
          </Typography>

          {/* Course/Service Name */}
          {courseName && (
            <Typography
              sx={{
                color: colors.fontDarkBlue,
                fontSize: { xs: '16px', md: '20px' },
                fontWeight: 600
              }}
              dangerouslySetInnerHTML={{
                __html: isCourse ? `Course: ${courseName}` : courseName
              }}
            />

          )}

          {/* Status Messages */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Box 
                sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  bgcolor: colors.successGreen,
                  animation: 'pulse 2s infinite'
                }} 
              />
              <Typography sx={{ color: colors.fontLight, fontSize: '12px' }}>
                Verifying payment status...
              </Typography>
            </Box>
            
            <Typography sx={{ color: colors.fontLight, fontSize: '12px', mt: 1 }}>
              Please do not close the window or refresh the page.
            </Typography>
          </Box>

          {/* CSS Animation */}
          <style>
            {`
              @keyframes pulse {
                0% { opacity: 1; }
                50% { opacity: 0.3; }
                100% { opacity: 1; }
              }
            `}
          </style>
        </CardContent>
      </Card>
    </Backdrop>
  );
};

export default PaymentProcessingOverlay;